import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, filter, map, type Observable, type OperatorFunction, throwError } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { StorageService, type WorkflowType } from '@td/core';
import type {
  CommonLogin,
  FederalLogin,
  LtsIncidentsList,
  LtsLogin,
  ParkingLogin,
  TicketsJwtPayload,
  TowingLogin,
} from '../../types';
import { AlertService } from '@td/components/alert';
import {
  AnalyticsCategories,
  AnalyticsDescriptions,
  AnalyticsEvents,
  AnalyticsLabels,
  trackEvent,
} from '@td/google-analytics';
import { SKIP_LTS_LOGOUT_FOR_LOGIN } from '../../http/http-context-tokens';

type Token = { accessToken: string };
export type AuthResponse = TicketsJwtPayload & Token;

function decode(): OperatorFunction<Token, AuthResponse> {
  return (source$) =>
    source$.pipe(
      map(({ accessToken }) => {
        const decoded = jwtDecode<TicketsJwtPayload>(accessToken);
        return { ...decoded, accessToken };
      }),
    );
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly storage = inject(StorageService);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly alert = inject(AlertService);

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(({ urlAfterRedirects }) => {
        const path = urlAfterRedirects.split('?')[0];
        if (!path.startsWith('/login')) return;
        if (path === '/login' || path.endsWith('/switcher')) return;
        this.saveLoginUrl(path);
      });
  }

  saveLoginUrl(url: string): void {
    this.storage.setItem('td:loginUrl', url);
  }

  commonLogin(request: CommonLogin): Observable<AuthResponse> {
    return this.http.post<Token>(`api/v1/tickets/auth/common/token`, request).pipe(
      decode(),
      catchError((error) => throwError(() => new Error(`Common login failed: ${JSON.stringify(error)}`))),
    );
  }

  ctwLogin(request: CommonLogin): Observable<AuthResponse> {
    return this.http.post<Token>(`api/v1/tickets/auth/ctw/token`, request).pipe(
      decode(),
      catchError((error) => throwError(() => new Error(`CTW login failed: ${JSON.stringify(error)}`))),
    );
  }

  aftLogin(request: CommonLogin): Observable<AuthResponse> {
    return this.http.post<Token>(`api/v1/tickets/auth/armed-forces-traffic/token`, request).pipe(
      decode(),
      catchError((error) => throwError(() => new Error(`Armed Forces Traffic login failed: ${JSON.stringify(error)}`))),
    );
  }

  parkingLogin(request: ParkingLogin): Observable<TicketsJwtPayload & { accessToken: string }> {
    return this.http.post<{ accessToken: string }>(`api/v1/tickets/auth/parking/token`, request).pipe(
      decode(),
      catchError((error) => throwError(() => new Error(`Parking login failed: ${JSON.stringify(error)}`))),
    );
  }

  towingLogin(request: TowingLogin): Observable<AuthResponse> {
    return this.http.post<Token>(`api/v1/tickets/auth/towing/token`, request).pipe(
      decode(),
      catchError((error) => throwError(() => new Error(`Towing login failed: ${JSON.stringify(error)}`))),
    );
  }

  federalLogin(request: FederalLogin): Observable<TicketsJwtPayload & { accessToken: string }> {
    return this.http.post<{ accessToken: string }>(`api/v1/tickets/auth/federal/token`, request).pipe(
      decode(),
      catchError((error) => throwError(() => new Error(`Federal login failed: ${JSON.stringify(error)}`))),
    );
  }

  ltsToken(request: LtsLogin): Observable<{ accessToken: string }> {
    return this.http.post<{ accessToken: string }>(`api/v1/lts/tickets/defendants/auth`, request, {
      context: new HttpContext().set(SKIP_LTS_LOGOUT_FOR_LOGIN, true),
    });
  }

  ltsTickets(): Observable<LtsIncidentsList> {
    return this.http.get<LtsIncidentsList>(`api/v1/lts/tickets`);
  }

  logout(loginUrl?: string): void {
    const savedLoginUrl = this.storage.getItem('td:loginUrl') ?? '/login';
    const redirectUrl = loginUrl ?? savedLoginUrl;
    const department = JSON.parse(this.storage.getItem('td:department') ?? '{}');
    this.router.navigate([redirectUrl], { queryParams: { id: department?.details?.governmentId } });
  }

  navigateToHome(flowType: WorkflowType): void {
    switch (flowType) {
      case 'CTW':
        this.router.navigate(['/ctw']);
        break;
      case 'PARKING':
        this.router.navigate(['/citation/parking-incident']);
        break;
      case 'TOWING':
        this.router.navigate(['/citation/towing-incident']);
        break;
      case 'LTS':
        this.router.navigate(['/lts']);
        break;
      case 'TRAFFIC':
      case 'MISDEMEANOR':
      case 'ARMED_FORCES_TRAFFIC':
      case 'FEDERAL':
      case 'FIRE_ENFORCEMENT':
        this.router.navigate(['/citation']);
        break;
    }
    this.alert.success('alert.success.title', 'alert.success.text');
  }

  sendLoginEvent(incidentId: string): void {
    const department = JSON.parse(this.storage.getItem('td:department') ?? '{}');
    trackEvent(AnalyticsEvents.Login, AnalyticsCategories.Login, AnalyticsLabels.Login, AnalyticsDescriptions.Login, {
      id: incidentId || '',
      departmentId: department?.details?.governmentId || '',
    });
  }
}
